import React, { useState, useEffect } from "react";
import Menu from "../Menu";
import { http } from "../../services/http";
import { useAlert } from "react-alert";
import TableRow from "@material-ui/core/TableRow";
import {
  PagingState,
  EditingState,
  CustomPaging,
  FilteringState,
  IntegratedFiltering,
  SelectionState,
  IntegratedSelection,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  Table,
  TableHeaderRow,
  PagingPanel,
  TableEditRow,
  TableEditColumn,
  TableFilterRow,
  TableColumnVisibility,
  TableSelection,
} from "@devexpress/dx-react-grid-material-ui";
import {
  QueryString,
  Create,
  Update,
  Remove,
  RemoveBulk,
  ExportXlsx,
  TIMER_FILTER,
  APPNAME_GERACAOXML,
} from "../utils/react_grid_utils";
import { ChNumberTypeProvider } from "../utils/react_grid_ch_number_column";
import { AbasMatrizConvenioTypeProvider } from "../utils/react_grid_abas_matrizconvenio_column";
import { ConveniosTypeProvider } from "../utils/react_grid_convenios_column";
import { TipoAcomodacaoTypeProvider } from "../utils/react_grid_tipo_acomodacao_column";
import { editColumnMessages } from "../utils/react_grid_edit_command_localization";
import Import from "../utils/import";
import authenticatedUserContext from "../security/AuthenticatedUserContext";
import Header from "../Header";
import MatrizDetailModal from "./MatrizDetailModal";

const getRowId = (row) => row.id;
const api_resource = "matriz";

export default function CnesCnpjTable() {
  const alert = useAlert();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [totalItens, setTotalItens] = useState(10);
  const [rows, setRows] = useState([]);
  const [selection, setSelection] = useState([]);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [tableColumnExtensions] = useState([
    { columnName: "id", width: 60 },
    { columnName: "convenio", width: 200 },
    { columnName: "plano", width: 350 },
    { columnName: "tabela", width: 150, editingEnabled: false },
    { columnName: "aba", width: 150 },
    { columnName: "tipo_acomodacao", width: 170 },
    { columnName: "ch", width: 80 },
  ]);
  const [defaultHiddenColumnNames] = useState(["id"]);
  const [filteringStateColumnExtensions] = useState([{ columnName: "ch", filteringEnabled: false }]);
  const [editingRowIds, setEditingRowIds] = useState([]);
  const [addedRows, setAddedRows] = useState([]);
  const [rowChanges, setRowChanges] = useState({});
  const [filtersApi, setFiltersApi] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  function get(page, filters) {
    const queryString = QueryString(page, pageSize, filters);
    http.get(`${api_resource}` + queryString).then(
      (response) => {
        setRows(response?.data?.data);
        setTotalItens(response?.data?.total);
        setSelection([]);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  useEffect(() => {
    get(0, []);
  }, []);

  const [timer, setTimer] = useState(null);

  function handleFilters(filters) {
    clearTimeout(timer);
    setFiltersApi(filters);

    setTimer(
      setTimeout(function () {
        get(pageIndex, filters);
      }, TIMER_FILTER)
    );
  }

  function handlePage(page) {
    setPageIndex(page);
    get(page, filtersApi);
  }

  const changeAddedRows = (value) => {
    const initialized = value.map((row) => (Object.keys(row).length ? row : {}));
    setAddedRows(initialized);
  };

  const commitChanges = ({ added, changed, deleted }) => {
    let changedRows;
    if (added) {
      Create(api_resource, added[0], alert).then((response) => {
        var new_row = response.data[0];
        const startingAddedId = rows.length > 0 ? rows[rows.length - 1].id + 1 : 0;
        changedRows = [
          ...added.map((row, index) => ({
            id: startingAddedId + index,
            ...new_row,
          })),
          ...rows,
        ];
        setRows(changedRows);
      });
    }
    if (changed) {
      changedRows = rows.map((row) => (changed[row.id] ? { ...row, ...changed[row.id] } : row));
      var item = changedRows.find((row) => changed[row.id]);
      Update(api_resource, item, alert).then((response) => {
        var changed_row = response.data[0];
        changedRows = rows.map((row) => (changed[row.id] ? { ...row, ...changed_row } : row));
        setRows(changedRows);
      });
    }
    if (deleted) {
      var confirmado = window.confirm("Confirma a exclusão?");
      if (confirmado === true) {
        Remove(api_resource, deleted[0], alert);
        const deletedSet = new Set(deleted);
        changedRows = rows.filter((row) => !deletedSet.has(row.id));
        setSelection((prev) => prev.filter((id) => !deletedSet.has(id)));
      } else {
        changedRows = rows;
      }
      setRows(changedRows);
    }
  };

  function handleBulkDelete() {
    if (!selection.length || deletingBulk) {
      return;
    }

    const quantidade = selection.length;
    const mensagem =
      quantidade === 1
        ? "Confirma a exclusão do registro selecionado?"
        : `Confirma a exclusão dos ${quantidade} registros selecionados?`;

    if (!window.confirm(mensagem)) {
      return;
    }

    setDeletingBulk(true);
    RemoveBulk(api_resource, selection, alert)
      .then(() => {
        const deletedSet = new Set(selection);
        setRows((prev) => prev.filter((row) => !deletedSet.has(row.id)));
        setSelection([]);
        get(pageIndex, filtersApi);
      })
      .finally(() => {
        setDeletingBulk(false);
      });
  }

  function openDetail(row) {
    setSelectedRow(row);
    setDetailOpen(true);
  }

  function closeDetail() {
    setDetailOpen(false);
    setSelectedRow(null);
  }

  function onDetailSaved(saved) {
    setRows((prev) => prev.map((row) => (row.id === saved.id ? { ...row, ...saved } : row)));
    setSelectedRow((prev) => (prev && prev.id === saved.id ? { ...prev, ...saved } : prev));
  }

  const TableRowComponent = ({ row, ...restProps }) => (
    <TableRow
      {...restProps}
      style={{ cursor: "pointer", ...(restProps.style || {}) }}
      onClick={(event) => {
        // Evita abrir o modal ao clicar nos botões/checkbox da grade
        if (
          event.target.closest("button") ||
          event.target.closest("a") ||
          event.target.closest("input") ||
          event.target.closest("label") ||
          event.target.closest(".MuiIconButton-root") ||
          event.target.closest(".MuiCheckbox-root") ||
          event.target.closest(".MuiTableSelection-cell")
        ) {
          return;
        }
        openDetail(row);
      }}
    />
  );

  var columns = [
    { name: "id", title: "ID" },
    { name: "convenio", title: "Convênio" },
    { name: "plano", title: "Plano" },
    { name: "tabela", title: "Tabela" },
    { name: "aba", title: "Aba" },
    { name: "tipo_acomodacao", title: "Tipo acomodação" },
    { name: "ch", title: "CH" },
  ];

  const canEdit = authenticatedUserContext.allow_edit(APPNAME_GERACAOXML) === true;

  return (
    <div id="wrapper">
      <Menu />
      <div id="page-wrapper" className="gray-bg">
        <Header />
        <div className="row wrapper border-bottom white-bg page-heading">
          <div className="col-lg-8">
            <h2>Matriz</h2>
          </div>
          <div className="col-lg-4" style={{ textAlign: "right" }}>
            {canEdit && selection.length > 0 ? (
              <button
                className="btn btn-danger btn-sm"
                style={{ marginTop: "20px", marginRight: "8px" }}
                type="button"
                disabled={deletingBulk}
                onClick={handleBulkDelete}
              >
                {deletingBulk
                  ? "Excluindo…"
                  : `Excluir selecionados (${selection.length})`}
              </button>
            ) : null}
            <button
              className="btn btn-primary btn-sm"
              style={{ marginTop: "20px" }}
              type="button"
              onClick={() => ExportXlsx(api_resource, alert)}
            >
              Exportar
            </button>
            <Import
              api_resource={api_resource}
              alert={alert}
              appname={APPNAME_GERACAOXML}
              confirmPreview={true}
              onFinish={() => get(0, filtersApi)}
            />
          </div>
        </div>
        <div className="wrapper wrapper-content animated fadeInRight">
          <div className="row">
            <div className="col-lg-12">
              <div className="ibox ">
                <div className="ibox-content">
                  <Grid rows={rows} columns={columns} getRowId={getRowId}>
                    <PagingState
                      currentPage={pageIndex}
                      pageSize={pageSize}
                      onCurrentPageChange={handlePage}
                    />
                    <EditingState
                      editingRowIds={editingRowIds}
                      onEditingRowIdsChange={setEditingRowIds}
                      rowChanges={rowChanges}
                      onRowChangesChange={setRowChanges}
                      addedRows={addedRows}
                      onAddedRowsChange={changeAddedRows}
                      onCommitChanges={commitChanges}
                      columnExtensions={tableColumnExtensions}
                    />
                    <FilteringState
                      onFiltersChange={handleFilters}
                      columnExtensions={filteringStateColumnExtensions}
                    />
                    <IntegratedFiltering />
                    <SelectionState
                      selection={selection}
                      onSelectionChange={setSelection}
                    />
                    <IntegratedSelection />
                    <Table
                      columnExtensions={tableColumnExtensions}
                      rowComponent={TableRowComponent}
                    />
                    {canEdit ? <TableSelection showSelectAll /> : null}
                    <TableHeaderRow />
                    <TableColumnVisibility defaultHiddenColumnNames={defaultHiddenColumnNames} />
                    <ChNumberTypeProvider for={["ch"]} />
                    <AbasMatrizConvenioTypeProvider for={["aba"]} />
                    <ConveniosTypeProvider for={["convenio"]} />
                    <TipoAcomodacaoTypeProvider for={["tipo_acomodacao"]} />
                    <TableEditRow />
                    {canEdit ? (
                      <TableEditColumn
                        showAddCommand={!addedRows.length}
                        showEditCommand
                        showDeleteCommand
                        messages={editColumnMessages}
                      />
                    ) : (
                      <></>
                    )}
                    <CustomPaging totalCount={totalItens} />
                    <PagingPanel />
                    <TableFilterRow />
                  </Grid>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MatrizDetailModal
        open={detailOpen}
        row={selectedRow}
        alert={alert}
        onClose={closeDetail}
        onSaved={onDetailSaved}
      />
    </div>
  );
}
